import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';
import { NumericProps } from '@swyftx/aviary/atoms/Typography/Numeric/Numeric.styles';
import { EnhancedTooltip } from '@swyftx/aviary/molecules/EnhancedTooltip/EnhancedTooltip';
import { calculateMarginRequirements } from '@swyftx/margin/build/utils';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { useMarginAccount } from 'src/lib/margin/hooks/account';

type Props = {
  title: string;
  lots: string;
  price: string;
  contractSize?: number;
  maxLeverage?: number;
  color?: 'primary' | 'dynamic';
  valueOnly?: boolean;
};

const TotalMarginRequired: React.FC<Props> = ({
  title,
  lots,
  price,
  contractSize,
  maxLeverage,
  valueOnly = false,
  color = 'dynamic',
}) => {
  const { availableMargin, currency } = useMarginAccount();
  const { t } = useTranslation('margin');

  const estimatedMargin = useMemo(
    () => calculateMarginRequirements({ lots: Number(lots), orderPrice: price, contractSize, leverage: maxLeverage }),
    [contractSize, lots, maxLeverage, price],
  );

  const hasExceededMarginRequirements = useMemo(
    () => Big(estimatedMargin).gt(availableMargin),
    [availableMargin, estimatedMargin],
  );

  const usedMarginColor = useMemo((): NumericProps['color'] => {
    if (estimatedMargin.eq(0)) return 'placeholder';
    if (color === 'primary') return 'primary';

    if (estimatedMargin.eq(0)) {
      return 'primary';
    } else if (hasExceededMarginRequirements) {
      return 'error';
    } else {
      return 'primary';
    }
  }, [color, estimatedMargin, hasExceededMarginRequirements]);

  return (
    <FlexLayout direction='column'>
      <FlexLayout
        className='w-full'
        alignItems='start'
        justifyContent={valueOnly ? 'end' : 'space-between'}
        spacing={8}
      >
        {!valueOnly && (
          <EnhancedTooltip
            title={t('position.totalMarginRequired.tooltip')}
            name={t('position.totalMarginRequired.name')}
            side='left'
          >
            <Body color='secondary' underline='dashed' className='flex cursor-pointer gap-4 whitespace-nowrap'>
              {title}
            </Body>
          </EnhancedTooltip>
        )}
        <FlexLayout direction='column' alignItems='end'>
          <Numeric weight='bold' color={usedMarginColor}>
            ≈{' '}
            {formatCurrency(estimatedMargin.round(2, 0), currency, {
              appendCode: true,
              hideCode: false,
            })}
          </Numeric>
        </FlexLayout>
      </FlexLayout>
      <Numeric color='secondary' className='text-right' weight='bold'>
        /{formatCurrency(availableMargin, currency, { appendCode: true, hideCode: false })}
      </Numeric>
    </FlexLayout>
  );
};

export { TotalMarginRequired };
